import React, { useEffect, useState } from "react";
import { TrendingUp } from "lucide-react";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import ConfirmPlanChangeModal from "./ConfirmPlanChangeModal";
import { downgradeConfirmationCopy } from "./downgradeCopy";
import { billingAPI } from "../../services/api/billing";
import { useToast } from "../../contexts/ToastContext";

export default function DowngradeModal({
  open,
  onClose,
  currentCode,
  currentStatus,
  currentPeriodEnd,
  currentPlanName,
  onSuccess,
}) {
  const { showToast } = useToast();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(null);
  // The plan the user picked from the list but hasn't confirmed yet —
  // clicking "Schedule downgrade" no longer calls the API directly (an
  // account owner had a PAYG downgrade scheduled 2 seconds after opening
  // this modal). Only the confirm step below calls downgradePlan.
  const [confirmingPlan, setConfirmingPlan] = useState(null);

  useEffect(() => {
    if (!open) return;
    billingAPI.listPlans().then(list => {
      const current = list.find(p => p.code === currentCode);
      const currentPrice = current?.monthly_price_cents ?? Infinity;
      setPlans(list.filter(p =>
        p.code !== currentCode &&
        p.is_public &&
        (p.monthly_price_cents ?? 0) < currentPrice
      ));
    });
  }, [open, currentCode]);

  useEffect(() => {
    if (!open) setConfirmingPlan(null);
  }, [open]);

  const handleConfirm = async (plan) => {
    try {
      setLoading(plan.id);
      await billingAPI.downgradePlan(plan.id);
      showToast(currentStatus === "past_due" ? "Switched to Pay as you go" : "Downgrade scheduled at period end", "success");
      setConfirmingPlan(null);
      onClose(); onSuccess?.();
    } catch (e) {
      showToast(e.response?.data?.error || "Downgrade failed", "error");
    } finally {
      setLoading(null);
    }
  };

  if (!open) return null;

  if (confirmingPlan) {
    const { title, body } = downgradeConfirmationCopy({
      currentStatus,
      currentPlanName,
      currentPeriodEnd,
      targetPlan: confirmingPlan,
    });

    return (
      <ConfirmPlanChangeModal
        open
        title={title}
        confirmLabel="Confirm downgrade"
        cancelLabel="Back"
        onCancel={() => setConfirmingPlan(null)}
        onConfirm={() => handleConfirm(confirmingPlan)}
        loading={loading === confirmingPlan.id}
      >
        <p className="text-sm text-muted-foreground">{body}</p>
      </ConfirmPlanChangeModal>
    );
  }

  return (
    <Modal title="Downgrade plan" onClose={onClose} maxWidth="max-w-lg">
      <p className="text-sm text-muted-foreground">
        {currentStatus === "past_due"
          ? "Your last renewal wasn't paid, so switching to Pay as you go happens right away — the unpaid invoice is cancelled and you won't be charged."
          : "Your current plan stays active until the end of the current period. The new plan takes effect on renewal."}
      </p>
      <div className="space-y-3 mt-2">
        {plans.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-4">No lower-tier plans available.</p>
        )}
        {plans.map(p => (
          <div key={p.id} className="flex items-center justify-between p-4 rounded-xl border border-border bg-muted/50/50">
            <div>
              <div className="text-sm font-semibold text-foreground">{p.name}</div>
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-0.5">
                <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
                {p.code === "pay_as_you_go" ? (
                  "Pay as you go · billed only for what you use"
                ) : (
                  <>
                    {p.monthly_price_cents
                      ? `$${(p.monthly_price_cents / 100).toFixed(0)}/mo`
                      : "Custom"}{" "}
                    · {p.monthly_credits} credits/mo
                  </>
                )}
              </div>
            </div>
            <Button
              variant="secondary"
              onClick={() => setConfirmingPlan(p)}
            >
              <span>Schedule downgrade</span>
            </Button>
          </div>
        ))}
      </div>
    </Modal>
  );
}
